/**
 * Pull the latest frontend build from the server.
 *
 * A plain `location.reload()` is not enough here. This is a PWA: the service
 * worker precaches the hashed assets and answers navigations from `html-cache`
 * (NetworkFirst), so a refresh can legitimately come back with the same old
 * bundle. The worker and its caches have to go first, then the reload fetches
 * index.html fresh and picks up the new asset hashes.
 *
 * Every step is best-effort - a failed cleanup must not stop the reload, which
 * is the part the user actually asked for.
 */

const SW_UNREGISTER_TIMEOUT_MS = 3000;

/**
 * Don't let a wedged service worker hold the reload hostage
 */
const withTimeout = (promise, ms) =>
  Promise.race([promise, new Promise((resolve) => setTimeout(resolve, ms))]);

/**
 * Drop the service worker so it stops serving the old precached bundle
 */
const unregisterServiceWorkers = async () => {
  if (!('serviceWorker' in navigator)) return;

  const registrations = await navigator.serviceWorker.getRegistrations();
  await Promise.all(registrations.map((registration) => registration.unregister()));
};

/**
 * Wipe Cache Storage (precached assets + the html/api runtime caches)
 */
const clearCacheStorage = async () => {
  if (!('caches' in window)) return;

  const keys = await caches.keys();
  await Promise.all(keys.map((key) => caches.delete(key)));
};

/**
 * Clear the worker + caches, then reload. Never resolves in practice - the
 * page navigates away.
 */
export const reloadToLatestBuild = async () => {
  try {
    await withTimeout(unregisterServiceWorkers(), SW_UNREGISTER_TIMEOUT_MS);
  } catch {
    // ignore - reload anyway
  }

  try {
    await withTimeout(clearCacheStorage(), SW_UNREGISTER_TIMEOUT_MS);
  } catch {
    // ignore - reload anyway
  }

  // index.html is served no-store (deploy.sh applies those nginx rules), so
  // with the worker gone a normal reload is guaranteed to hit the server
  window.location.reload();
};
